import React, { useState, useEffect, useCallback } from 'react';
import { Can } from '../components/Can';
import {
  Brain, Users, Shield, TrendingUp, TrendingDown, Target, BarChart3,
  Search, Play, Loader2, ChevronDown, ChevronUp, Zap, AlertTriangle,
  CheckCircle, XCircle, Activity, Cpu, Eye, RefreshCw, Clock,
  ArrowUpRight, ArrowDownRight, Minus, Award, Layers
} from 'lucide-react';

const API = '/api/agents';
function headers() {
  return { 'Authorization': 'Bearer ' + localStorage.getItem('token'), 'Content-Type': 'application/json' };
}

// ─── Shared Components ───

function Badge({ children, color = 'blue' }) {
  const colors = {
    green: 'bg-green-500/15 text-green-400 border-green-500/30',
    red: 'bg-red-500/15 text-red-400 border-red-500/30',
    yellow: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30',
    blue: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
    purple: 'bg-purple-500/15 text-purple-400 border-purple-500/30',
    orange: 'bg-orange-500/15 text-orange-400 border-orange-500/30',
  };
  return <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${colors[color] || colors.blue}`}>{children}</span>;
}

function SignalBadge({ signal }) {
  if (!signal) return null;
  const map = { bullish: ['green', '▲ Bullish'], lean_bullish: ['green', '▲ Lean Bull'], bearish: ['red', '▼ Bearish'], lean_bearish: ['red', '▼ Lean Bear'], neutral: ['yellow', '— Neutral'] };
  const [color, label] = map[signal] || ['yellow', signal];
  return <Badge color={color}>{label}</Badge>;
}

function RiskBadge({ level }) {
  const map = { minimal: 'green', low: 'green', moderate: 'yellow', high: 'orange', extreme: 'red' };
  return <Badge color={map[level] || 'yellow'}>{level}</Badge>;
}

function ScoreRing({ score, size = 48, label }) {
  const radius = (size - 6) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 70 ? '#22c55e' : score >= 50 ? '#3b82f6' : score >= 30 ? '#eab308' : '#ef4444';

  return (
    <div className="flex flex-col items-center gap-1">
      <svg width={size} height={size} className="transform -rotate-90">
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="3" />
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={color} strokeWidth="3"
          strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
          className="transition-all duration-700" />
      </svg>
      <div className="absolute flex items-center justify-center" style={{width: size, height: size}}>
        <span className="text-sm font-bold" style={{color}}>{score}</span>
      </div>
      {label && <span className="text-[10px] text-text-secondary">{label}</span>}
    </div>
  );
}

// ─── Agent Team Tab ───

function AgentTeamTab() {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/team`, { headers: headers() })
      .then(r => r.json())
      .then(d => { if (d.success) setTeam(d.team); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const agentIcons = { 'Analyst Alpha': Brain, 'Fundamentals Agent': BarChart3, 'Sentiment Agent': Activity, 'Risk Sentinel': Shield, 'Capital Allocator': Target, 'Market Scout': Search, 'Backtest Engine': Layers };
  const agentColors = { 'Analyst Alpha': 'text-purple-400', 'Fundamentals Agent': 'text-blue-400', 'Sentiment Agent': 'text-green-400', 'Risk Sentinel': 'text-red-400', 'Capital Allocator': 'text-yellow-400', 'Market Scout': 'text-orange-400', 'Backtest Engine': 'text-cyan-400' };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-accent-blue" /></div>;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {team.map(agent => {
        const Icon = agentIcons[agent.name] || Cpu;
        const color = agentColors[agent.name] || 'text-accent-blue';
        return (
          <div key={agent.name} className="bg-bg-secondary/50 rounded-xl border border-border-color/30 p-5 hover:border-accent-blue/30 transition-all">
            <div className="flex items-start gap-3 mb-3">
              <div className={`w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center`}>
                <Icon className={`w-5 h-5 ${color}`} />
              </div>
              <div className="flex-1">
                <div className="font-semibold text-text-primary text-sm">{agent.name}</div>
                <div className="text-xs text-text-secondary">{agent.role}</div>
              </div>
              <Badge color={agent.status === 'active' ? 'green' : 'yellow'}>{agent.status}</Badge>
            </div>
            <p className="text-xs text-text-secondary leading-relaxed">{agent.description}</p>
            {agent.aiPowered !== undefined && (
              <div className="mt-3 flex items-center gap-1.5">
                <div className={`w-1.5 h-1.5 rounded-full ${agent.aiPowered ? 'bg-green-400' : 'bg-yellow-400'}`} />
                <span className="text-[10px] text-text-secondary">{agent.aiPowered ? 'Claude AI enabled' : 'Rule-based mode'}</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Pipeline Analysis Tab ───

function PipelineTab() {
  const [symbol, setSymbol] = useState('AAPL');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);

  const runPipeline = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/pipeline/${symbol}`, { method: 'POST', headers: headers(), body: JSON.stringify({}) });
      const data = await res.json();
      if (data.success) setResult(data.report);
    } catch(e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => {
    fetch(`${API}/reports?limit=10`, { headers: headers() })
      .then(r => r.json())
      .then(d => { if (d.success) setHistory(d.reports); })
      .catch(() => {});
  }, [result]);

  const decision = result?.decision;
  const actionColors = { BUY: 'text-green-400', LEAN_BUY: 'text-green-300', AVOID: 'text-red-400', LEAN_AVOID: 'text-red-300', NEUTRAL: 'text-yellow-400' };

  return (
    <div className="space-y-5">
      {/* Input */}
      <div className="bg-bg-secondary/50 rounded-xl border border-border-color/30 p-5">
        <div className="flex items-center gap-3">
          <input value={symbol} onChange={e => setSymbol(e.target.value.toUpperCase())}
            className="bg-bg-primary border border-border-color/40 rounded-lg px-4 py-2.5 text-sm text-text-primary w-32 focus:outline-none focus:border-accent-blue/50"
            placeholder="SYMBOL" />
          <Can perm="action.agents.run_pipeline" fallback={
            <span className="text-xs text-text-muted px-2" title="Your role can't run the AI pipeline (costs money)">View only</span>
          }>
            <button onClick={runPipeline} disabled={loading || !symbol}
              className="px-5 py-2.5 bg-accent-blue/15 text-accent-blue rounded-lg text-sm font-medium hover:bg-accent-blue/25 disabled:opacity-40 transition-colors flex items-center gap-2">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              Run Full Pipeline
            </button>
          </Can>
          {result && <span className="text-xs text-text-secondary ml-auto">{result.pipelineMs}ms</span>}
        </div>
      </div>

      {/* Decision */}
      {decision && (
        <div className="bg-bg-secondary/50 rounded-xl border border-border-color/30 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold text-text-primary">{result.symbol} — Consensus</h3>
              <p className="text-xs text-text-secondary">All agents analyzed • Position size: {decision.positionSize}%</p>
            </div>
            <div className="text-right">
              <div className={`text-3xl font-black ${actionColors[decision.action] || 'text-text-primary'}`}>{decision.action}</div>
              <div className="text-xs text-text-secondary">{decision.confidence}% confidence • {decision.bullPercent}% bullish</div>
            </div>
          </div>

          {/* Agent reasons */}
          <div className="space-y-2 mt-4">
            {decision.reasons?.map((r, i) => (
              <div key={i} className="flex items-start gap-2 text-sm">
                <CheckCircle className="w-3.5 h-3.5 text-accent-blue mt-0.5 flex-shrink-0" />
                <span className="text-text-secondary">{r}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Individual Agent Reports */}
      {result?.reports && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Fundamentals */}
          {result.reports.fundamentals && (
            <div className="bg-bg-secondary/50 rounded-xl border border-border-color/30 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-blue-400" />
                  <span className="text-sm font-semibold text-text-primary">Fundamentals</span>
                </div>
                <SignalBadge signal={result.reports.fundamentals.signal} />
              </div>
              <div className="relative inline-flex"><ScoreRing score={result.reports.fundamentals.score || 50} /></div>
              <p className="text-xs text-text-secondary mt-2">{result.reports.fundamentals.reason}</p>
            </div>
          )}

          {/* Sentiment */}
          {result.reports.sentiment && (
            <div className="bg-bg-secondary/50 rounded-xl border border-border-color/30 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Activity className="w-4 h-4 text-green-400" />
                  <span className="text-sm font-semibold text-text-primary">Sentiment</span>
                </div>
                <SignalBadge signal={result.reports.sentiment.signal} />
              </div>
              <div className="relative inline-flex"><ScoreRing score={result.reports.sentiment.score || 50} /></div>
              <p className="text-xs text-text-secondary mt-2">{result.reports.sentiment.reason}</p>
              {result.reports.sentiment.headlines?.slice(0, 3).map((h, i) => (
                <p key={i} className="text-[11px] text-text-secondary/60 mt-1 truncate">• {h}</p>
              ))}
            </div>
          )}

          {/* Risk */}
          {result.reports.risk && (
            <div className="bg-bg-secondary/50 rounded-xl border border-border-color/30 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-red-400" />
                  <span className="text-sm font-semibold text-text-primary">Risk Sentinel</span>
                </div>
                <RiskBadge level={result.reports.risk.riskLevel} />
              </div>
              <div className="text-2xl font-bold text-text-primary mb-2">{result.reports.risk.riskScore}/100</div>
              <p className="text-xs text-text-secondary">{result.reports.risk.reason}</p>
              <p className="text-xs text-text-secondary/60 mt-1">Max position: {result.reports.risk.maxPositionSizePct}%</p>
            </div>
          )}

          {/* Allocation */}
          {result.reports.allocation && (
            <div className="bg-bg-secondary/50 rounded-xl border border-border-color/30 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-yellow-400" />
                  <span className="text-sm font-semibold text-text-primary">Capital Allocation</span>
                </div>
                <Badge color={result.reports.allocation.recommendation === 'full' ? 'green' : result.reports.allocation.recommendation === 'skip' ? 'red' : 'yellow'}>
                  {result.reports.allocation.recommendation}
                </Badge>
              </div>
              <div className="text-2xl font-bold text-text-primary mb-2">{result.reports.allocation.positionSizePct}%</div>
              {result.reports.allocation.adjustments?.map((a, i) => (
                <p key={i} className="text-[11px] text-text-secondary/60">• {a}</p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Recent History */}
      {history.length > 0 && (
        <div className="bg-bg-secondary/50 rounded-xl border border-border-color/30 p-5">
          <h3 className="text-sm font-semibold text-text-primary mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4 text-text-secondary" /> Recent Analyses
          </h3>
          <div className="space-y-2">
            {history.map((r, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-border-color/10 last:border-0">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-text-primary w-14">{r.symbol}</span>
                  <span className={`text-xs font-bold ${actionColors[r.decision?.action] || ''}`}>{r.decision?.action}</span>
                  <span className="text-xs text-text-secondary">{r.decision?.confidence}% conf</span>
                </div>
                <span className="text-xs text-text-secondary">{new Date(r.timestamp).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Backtest Tab ───

function BacktestTab() {
  const [symbol, setSymbol] = useState('AAPL');
  const [strategy, setStrategy] = useState('momentum');
  const [months, setMonths] = useState(6);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [strategies, setStrategies] = useState([]);

  useEffect(() => {
    fetch(`${API}/backtest/strategies`, { headers: headers() })
      .then(r => r.json())
      .then(d => { if (d.success) setStrategies(d.strategies); })
      .catch(() => {});
  }, []);

  const runBacktest = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/backtest/run`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ symbol, strategy, months })
      });
      const data = await res.json();
      if (data.success) setResult(data.result);
    } catch(e) { console.error(e); }
    setLoading(false);
  };

  const m = result?.metrics;

  return (
    <div className="space-y-5">
      {/* Controls */}
      <div className="bg-bg-secondary/50 rounded-xl border border-border-color/30 p-5">
        <div className="flex flex-wrap items-center gap-3">
          <input value={symbol} onChange={e => setSymbol(e.target.value.toUpperCase())}
            className="bg-bg-primary border border-border-color/40 rounded-lg px-4 py-2.5 text-sm text-text-primary w-28 focus:outline-none focus:border-accent-blue/50"
            placeholder="SYMBOL" />
          <select value={strategy} onChange={e => setStrategy(e.target.value)}
            className="bg-bg-primary border border-border-color/40 rounded-lg px-3 py-2.5 text-sm text-text-primary focus:outline-none">
            {strategies.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select value={months} onChange={e => setMonths(parseInt(e.target.value))}
            className="bg-bg-primary border border-border-color/40 rounded-lg px-3 py-2.5 text-sm text-text-primary focus:outline-none">
            <option value={3}>3 months</option>
            <option value={6}>6 months</option>
            <option value={12}>12 months</option>
          </select>
          <button onClick={runBacktest} disabled={loading}
            className="px-5 py-2.5 bg-accent-purple/15 text-accent-purple rounded-lg text-sm font-medium hover:bg-accent-purple/25 disabled:opacity-40 flex items-center gap-2">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            Run Backtest
          </button>
        </div>
      </div>

      {/* Results */}
      {m && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-bg-secondary/50 rounded-xl border border-border-color/30 p-4">
              <div className="text-xs text-text-secondary mb-1">Total Return</div>
              <div className={`text-xl font-bold ${m.totalReturn >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {m.totalReturn >= 0 ? '+' : ''}{m.totalReturn}%
              </div>
              <div className="text-xs text-text-secondary">${m.totalPnl?.toFixed(2)}</div>
            </div>
            <div className="bg-bg-secondary/50 rounded-xl border border-border-color/30 p-4">
              <div className="text-xs text-text-secondary mb-1">Win Rate</div>
              <div className="text-xl font-bold text-text-primary">{m.winRate}%</div>
              <div className="text-xs text-text-secondary">{m.winners}W / {m.losers}L ({m.totalTrades} trades)</div>
            </div>
            <div className="bg-bg-secondary/50 rounded-xl border border-border-color/30 p-4">
              <div className="text-xs text-text-secondary mb-1">Sharpe Ratio</div>
              <div className={`text-xl font-bold ${m.sharpeRatio >= 1 ? 'text-green-400' : m.sharpeRatio >= 0 ? 'text-yellow-400' : 'text-red-400'}`}>
                {m.sharpeRatio}
              </div>
              <div className="text-xs text-text-secondary">{m.sharpeRatio >= 1 ? 'Good' : m.sharpeRatio >= 0 ? 'Mediocre' : 'Poor'}</div>
            </div>
            <div className="bg-bg-secondary/50 rounded-xl border border-border-color/30 p-4">
              <div className="text-xs text-text-secondary mb-1">Max Drawdown</div>
              <div className="text-xl font-bold text-red-400">-{m.maxDrawdown}%</div>
              <div className="text-xs text-text-secondary">Profit Factor: {m.profitFactor}</div>
            </div>
          </div>

          {/* Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-bg-secondary/50 rounded-xl border border-border-color/30 p-5">
              <h3 className="text-sm font-semibold text-text-primary mb-3">Strategy Details</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-text-secondary">Strategy</span><span className="text-text-primary">{result.strategyName}</span></div>
                <div className="flex justify-between"><span className="text-text-secondary">Period</span><span className="text-text-primary">{result.period}</span></div>
                <div className="flex justify-between"><span className="text-text-secondary">Avg Hold</span><span className="text-text-primary">{m.avgHoldDays} days</span></div>
                <div className="flex justify-between"><span className="text-text-secondary">Avg Win</span><span className="text-green-400">${m.avgWin}</span></div>
                <div className="flex justify-between"><span className="text-text-secondary">Avg Loss</span><span className="text-red-400">${m.avgLoss}</span></div>
                <div className="flex justify-between"><span className="text-text-secondary">Initial Capital</span><span className="text-text-primary">${m.initialCapital?.toLocaleString()}</span></div>
                <div className="flex justify-between"><span className="text-text-secondary">Final Equity</span><span className={m.finalEquity >= m.initialCapital ? 'text-green-400' : 'text-red-400'}>${m.finalEquity?.toLocaleString()}</span></div>
              </div>
            </div>

            {/* Trade List */}
            <div className="bg-bg-secondary/50 rounded-xl border border-border-color/30 p-5 max-h-80 overflow-y-auto">
              <h3 className="text-sm font-semibold text-text-primary mb-3">Trades ({result.trades?.length})</h3>
              <div className="space-y-1.5">
                {result.trades?.map((t, i) => (
                  <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-border-color/10">
                    <span className="text-text-secondary">{t.entryDate}</span>
                    <span className="text-text-secondary">{t.holdDays}d</span>
                    <span className={`font-medium ${t.pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {t.pnl >= 0 ? '+' : ''}${t.pnl} ({t.pnlPct}%)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Scout (Screening) Tab ───

function ScoutTab() {
  const [symbols, setSymbols] = useState('AAPL,MSFT,NVDA,AMZN,GOOGL,META,TSLA,AMD,CRM,ORCL');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const runScreen = async () => {
    setLoading(true);
    try {
      const syms = symbols.split(',').map(s => s.trim().toUpperCase()).filter(Boolean);
      const res = await fetch(`${API}/scout/screen`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ symbols: syms, marketData: {} })
      });
      const data = await res.json();
      if (data.success) setResult(data.report);
    } catch(e) { console.error(e); }
    setLoading(false);
  };

  const gradeColors = { A: 'text-green-400', B: 'text-blue-400', C: 'text-yellow-400', D: 'text-orange-400', F: 'text-red-400' };

  return (
    <div className="space-y-5">
      <div className="bg-bg-secondary/50 rounded-xl border border-border-color/30 p-5">
        <div className="flex flex-col md:flex-row gap-3">
          <input value={symbols} onChange={e => setSymbols(e.target.value)}
            className="flex-1 bg-bg-primary border border-border-color/40 rounded-lg px-4 py-2.5 text-sm text-text-primary focus:outline-none focus:border-accent-blue/50"
            placeholder="AAPL,MSFT,NVDA,..." />
          <Can perm="action.agents.run_scout" fallback={
            <span className="text-xs text-text-muted px-2" title="Your role can't run the AI scout (costs money)">View only</span>
          }>
            <button onClick={runScreen} disabled={loading}
              className="px-5 py-2.5 bg-accent-orange/15 text-accent-orange rounded-lg text-sm font-medium hover:bg-accent-orange/25 disabled:opacity-40 flex items-center gap-2 whitespace-nowrap">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Screen ({symbols.split(',').filter(Boolean).length})
            </button>
          </Can>
        </div>
      </div>

      {result && (
        <>
          <div className="flex items-center gap-4 text-sm">
            <span className="text-text-secondary">Screened: <span className="text-text-primary font-medium">{result.symbolsScreened}</span></span>
            <span className="text-text-secondary">Tradeable: <span className="text-green-400 font-medium">{result.tradeableCount}</span></span>
            {result.topPicks?.length > 0 && (
              <span className="text-text-secondary">Top Picks: <span className="text-accent-blue font-medium">{result.topPicks.join(', ')}</span></span>
            )}
          </div>

          <div className="space-y-2">
            {result.results?.map((r, i) => (
              <div key={i} className={`bg-bg-secondary/50 rounded-xl border p-4 flex items-center gap-4 ${r.tradeable ? 'border-green-500/20' : 'border-border-color/20'}`}>
                <div className={`text-2xl font-black w-10 text-center ${gradeColors[r.grade] || 'text-text-secondary'}`}>{r.grade}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-text-primary">{r.symbol}</span>
                    <span className="text-xs text-text-secondary">Score: {r.composite}/100</span>
                    {r.tradeable && <Badge color="green">Tradeable</Badge>}
                    {r.vcp?.detected && <Badge color="purple">VCP Pattern</Badge>}
                  </div>
                  <div className="flex gap-4 mt-1 text-xs text-text-secondary">
                    <span>CANSLIM: {r.canslim?.score}</span>
                    <span>VCP: {r.vcp?.score}</span>
                    <span>Breadth: {r.breadth?.score}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="w-12 h-12 relative inline-flex"><ScoreRing score={r.composite} /></div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Main Page ───

export default function AgentTeam() {
  const [tab, setTab] = useState('team');

  const tabs = [
    { id: 'team', label: 'Agent Team', icon: Users },
    { id: 'pipeline', label: 'Pipeline Analysis', icon: Zap },
    { id: 'scout', label: 'Market Scout', icon: Search },
    { id: 'backtest', label: 'Backtesting', icon: Layers },
  ];

  return (
    <div className="min-h-screen bg-bg-primary">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-accent-blue/15 flex items-center justify-center">
                <Brain className="w-5 h-5 text-accent-blue" />
              </div>
              AI Agent Team
            </h1>
            <p className="text-sm text-text-secondary mt-1">7 agents working together for smarter trading decisions</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                tab === t.id ? 'bg-accent-blue/15 text-accent-blue' : 'text-text-secondary hover:bg-white/5'
              }`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {tab === 'team' && <AgentTeamTab />}
        {tab === 'pipeline' && <PipelineTab />}
        {tab === 'scout' && <ScoutTab />}
        {tab === 'backtest' && <BacktestTab />}
      </div>
    </div>
  );
}
